import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ChevronDownIcon } from '@fronton/icons-react';
import log from 'loglevel';
import { RegularButton, Modal, ModalFooter, ModalContent, Typography, LinkButton } from 'fronton-react';
import { StoreDetail } from 'features/store/types';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { analyticsUtils } from 'utils';
import { getTitle } from 'features/page-model/ducks/get-model';
import { modalHeader, modalCaption, modalFooter } from '../styles';
import { button, buttons, buttonDay, holiday, holidayCaption } from './style';
import { OperationalTime } from './operational-time';
import { days, seoDays, getOperationalTime, getCurrentOperationalTime, getWorkingTime } from './utils';

type Props = {
  storeDetail: StoreDetail;
  name?: string;
};

export function StoreSchedule({ storeDetail = {}, name }: Props) {
  const {
    buildingYardOperationalTime,
    differentOperationalTime,
    differentOperationalTimeName,
    gardenCenterOperationalTime,
    pickupOperationalTime,
    holidaysOperationalTime,
  } = storeDetail;
  const today = (new Date().getDay() || 7) - 1;

  const title = useSelector(getTitle);
  const { t } = useTranslation();

  const { mobile } = useBreakpoints() ?? {};
  const [open, setOpen] = useState(false);
  const [weekday, setWeekday] = useState(today);

  const ref = useRef<null | HTMLDivElement>(null);

  const currentOperationalTime = getCurrentOperationalTime(storeDetail);
  const workingTime = getWorkingTime(currentOperationalTime);
  const workingHours = t('workingHours');
  const close = t('ui.close');
  const openFrom = t('openFrom');

  const onClose = () => {
    setOpen(false);
    setWeekday(today);

    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'shopPage', // блок, в котором расположен элемент
      elementName: `${title} / ${workingHours}`, // название блока
      elementText: close, // текст элемента
    });
  };

  const onOpen = () => {
    setOpen(true);

    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'shopPage', // блок, в котором расположен элемент
      elementName: title, // название блока
      elementText: `${openFrom} ${workingTime}`, // текст элемента
    });
  };

  const selectWeekDay = useCallback(
    (index: number) => {
      setWeekday(index);
      analyticsUtils.sendEvent('GA:clickButton', {
        elementType: 'button',
        elementBlock: 'shopPage', // блок, в котором расположен элемент
        elementName: `${title} / ${workingHours}`, // название блока
        elementText: days[index], // текст элемента
      });
    },
    [title, workingHours],
  );

  useEffect(() => {
    if (open && ref?.current && ref.current.scrollIntoView) {
      try {
        ref.current.scrollIntoView({ behavior: 'smooth', inline: 'center' });
      } catch (e) {
        log.error(e);
      }
    }
  }, [open, weekday]);

  return (
    <>
      <LinkButton
        onClick={onOpen}
        size="m"
        variant="accent"
        data-qa="schedule-opener"
        iconRight={<ChevronDownIcon />}
        className={button}
        withExtraClickZone
      >
        {`${openFrom} ${workingTime}`}
      </LinkButton>
      {days.map((day, index) => {
        const time = getOperationalTime(storeDetail, index);
        return <meta key={day} itemProp="openingHours" content={`${seoDays[index]} ${time}`} />;
      })}
      <Modal
        onClose={onClose}
        size="s"
        variant={mobile ? 'full-screen' : 'simple'}
        data-qa="store-schedule-modal"
        show={open}
      >
        <div className={modalHeader}>
          <Typography color="text-secondary" variant="caption">
            {name}
          </Typography>
          <Typography className={modalCaption} variant={mobile ? 'h2' : 'h1'}>
            {workingHours}
          </Typography>
        </div>
        <ModalContent>
          <div className={buttons}>
            {days.map((day, index) => {
              const isChoosen = index === weekday;
              return (
                <div key={day} ref={isChoosen ? ref : null}>
                  <RegularButton
                    className={buttonDay}
                    data-qa={`button-day-${day}`}
                    onClick={() => selectWeekDay(index)}
                    size="s"
                    variant={isChoosen ? 'primary' : 'secondary'}
                  >
                    {day}
                  </RegularButton>
                </div>
              );
            })}
          </div>
          <OperationalTime title={t('tradeFloor')} time={getOperationalTime(storeDetail, weekday)} />
          {buildingYardOperationalTime && (
            <OperationalTime title={t('buildingYard')} time={buildingYardOperationalTime} />
          )}
          {gardenCenterOperationalTime && (
            <OperationalTime title={t('gardenCenter')} time={gardenCenterOperationalTime} />
          )}
          {pickupOperationalTime && <OperationalTime title={t('pickup')} time={pickupOperationalTime} />}
          {differentOperationalTime && (
            <OperationalTime
              title={differentOperationalTimeName || t('pickpoint')}
              time={differentOperationalTime}
            />
          )}
          {holidaysOperationalTime && (
            <div className={holiday}>
              <Typography className={holidayCaption} variant="m" size="body_accent">
                {t('holidaysWorkingHours')}
              </Typography>
              <Typography variant="m" size="body_long">
                {holidaysOperationalTime}
              </Typography>
            </div>
          )}
        </ModalContent>
        <ModalFooter className={modalFooter}>
          <RegularButton data-qa="schedule-closer" onClick={onClose} size="l" wide variant="secondary">
            {close}
          </RegularButton>
        </ModalFooter>
      </Modal>
    </>
  );
}
